import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import NotificationAlert from "../../components/notification/NotificationAlert"
import { Box, Checkbox, Grid, IconButton, makeStyles } from "@material-ui/core"
import NCText from "../../shared/NCText"
import NCInput from "../../shared/NCInput"
import { DetailIcon, SearchIcon } from "../../libs/icons"
import NCCalendarButton from "../../shared/NCCalendarButton"
import { formatDateString, formatIncidentDate } from "../../libs/utils"
import useDebounce from "../../libs/hooks/Debounce"
import { SEARCH_CONFIG, TABLE_CONFIG } from "../../libs/config"
import {
  useGetPendingAmount,
  useGetTripReportList
} from "../../services/queries"
// import { NOAH_CARES, STORAGE } from "../../libs/storage"
import NCTable from "../../shared/table/NCTable"
import NCLoader from "../../shared/NCLoader"
import { useNavigate } from "react-router-dom"
import NCButton from "../../shared/NCButton"
import { usePayPendingAmount } from "../../services/mutations"
import { toast } from "react-toastify"
import NCModal from "../../shared/NCModal"
import { useQueryClient } from "react-query"

const useStyles = makeStyles(() => ({
  center: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  pay: {
    marginLeft: "10px"
  }
}))

const MyReports = () => {
  const [query, setQuery] = useState("")
  const cls = useStyles()
  const { t } = useTranslation()
  const nav = useNavigate()
  const [selectedDate, setSelectedDate] = useState(formatIncidentDate(new Date(), "YYYY-MM-DD"))
  const [page, setPage] = useState(1)
  const [ordering, setOrdering] = useState("id")
  const [pageSize, setPageSize] = useState(TABLE_CONFIG.PAGE_SIZE)
  const [showPayModal, setShowPayModal] = useState(false)
  const [selectedReports, setSelectedReports] = useState([])

  const queryClient = useQueryClient()
  const payPendingAmount = usePayPendingAmount()
  const debouncedSearchTerm = useDebounce(query, SEARCH_CONFIG.TIME_IN_MILLI)
  const { isLoading, data, refetch } = useGetTripReportList(
    page,
    debouncedSearchTerm,
    pageSize,
    {
      ordering,
      date: selectedDate || ""
    }
  )

  const {
    next,
    previous,
    total_pages,
    results: reportsData
  } = data?.data?.data || {}

  const { data: unpaidAmountData, refetch: refetchUnpaidAmount } =
    useGetPendingAmount()

  const onNext = () => {
    setPage(prev => (next ? prev + 1 : prev))
  }
  const onPrev = () => {
    setPage(prev => (previous ? prev - 1 : prev))
  }

  // Same checkbox pattern as /manage-reports
  const isReportSelected = id => selectedReports.findIndex(item => item.id === id) > -1

  const onCheckChange = report => {
    if (isReportSelected(report.id)) {
      setSelectedReports(prev => prev.filter(item => item.id !== report.id))
    } else {
      setSelectedReports(prev => [...prev, report])
    }
  }

  const isPayable = r => r.report_status === "SENT" || r.report_status === "PENDING"

  const getIdCell = cell => (
    <Grid container alignItems="center">
      <Checkbox
        color="primary"
        onChange={() => onCheckChange(cell)}
        checked={isReportSelected(cell.id)}
        disabled={!isPayable(cell)}
      />
      <NCText>{cell.id}</NCText>
    </Grid>
  )

  const getActionColumn = cell => {
    return (
      <div className="action-icons">
        <NCText>{cell?.drop_off_address?.address}</NCText>
        <IconButton onClick={() => nav(`/my-reports/${cell.id}`)}>
          <DetailIcon />
        </IconButton>
      </div>
    )
  }

  const getDriverName = cell => {
    return (
      <div>
        <NCText>{cell?.driver?.name}</NCText>
      </div>
    )
  }

  const getClientName = cell => {
    return (
      <div>
        <NCText>{cell?.trip_client_name || cell?.client_request?.client_name}</NCText>
      </div>
    )
  }

  const getReportStatus = cell => {
    return (
      <div>
        <NCText>
          {cell?.report_status === "SENT" ? "PENDING" : cell?.report_status}
        </NCText>
      </div>
    )
  }

  const columns = [
    {
      Header: t("trip_id"),
      id: "id",
      accessor: cell => getIdCell(cell),
      disableSortBy: true
    },
    {
      Header: t("driver"),
      id: "driver.name",
      accessor: cell => getDriverName(cell),
      disableSortBy: true
    },
    {
      Header: t("client_name"),
      id: "client",
      accessor: cell => getClientName(cell),
      disableSortBy: true
    },
    {
      Header: t("status"),
      id: "status",
      accessor: cell => getReportStatus(cell),
      defaultCanFilter: false,
      disableSortBy: true
    },
    {
      Header: t("pickup_address"),
      accessor: cell => cell.pickup_address.address,
      disableSortBy: true
    },
    {
      Header: t("pickup_datetime"),
      accessor: cell => formatDateString(cell.pickup_time),
      disableSortBy: true
    },

    {
      Header: t("drop_address"),
      id: "action",
      accessor: cell => getActionColumn(cell),
      disableSortBy: true
    }
  ]

  const onPayClick = async () => {
    setShowPayModal(false)
    const tripIds = selectedReports.map(r => r.id)
    const payResponse = await payPendingAmount.mutateAsync({ trip_ids: tripIds })
    if ((await payResponse).status === 200 && payResponse?.data?.message) {
      queryClient.invalidateQueries({
        queryKey: ["getTripReportList", "useGetPendingAmount"]
      })
      toast.success(t("payment_success"))
      setSelectedReports([])
      refetch()
      refetchUnpaidAmount()
    } else {
      toast.error(payResponse?.data?.message)
    }
  }

  const payableCount = (reportsData || []).filter(isPayable).length
  const selectedPayableCount = selectedReports.filter(isPayable).length
  const selectedAmount = selectedReports
    .filter(isPayable)
    .reduce((sum, r) => sum + (Number(r.invoice_total) || 0), 0)

  return (
    <Box>
      <Box mb={2}>
        <Grid container justifyContent="space-between" mb={20}>
          <Grid>
            <NCText variant={"h4"}>{t("my_reports")}</NCText>
          </Grid>
          
          <Grid item>
            <Grid container spacing={2}>
              {payableCount > 0 && (
                <Grid item>
                  <Grid container className={cls.center}>
                    <Grid item>
                      <NCText>
                        {selectedPayableCount > 0
                          ? `${t("unpaid_amount")}: $${selectedAmount.toFixed(2)}`
                          : `${t("unpaid_amount")}: $${unpaidAmountData?.data?.data?.total_unpaid_amount || 0}`}
                      </NCText>
                    </Grid>
                    <Grid item className={cls.pay}>
                      <NCButton
                        variant="contained"
                        color="primary"
                        disabled={selectedPayableCount === 0}
                        onClick={() => setShowPayModal(true)}
                      >
                        {t("pay_now")} {selectedPayableCount > 0 ? `(${selectedPayableCount})` : ""}
                      </NCButton>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              <Grid item>
                <NCInput
                  value={query}
                  onChange={e => {
                    setQuery(e.target.value)
                    setPage(1)
                  }}
                  placeholder={t("search_here")}
                  startIcon={SearchIcon}
                  className="rounded"
                />
              </Grid>
              <Grid item>
                <NotificationAlert />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent={"flex-end"}
        alignItems="flex-end"
      >
        <NCCalendarButton
          selectedDate={selectedDate => {
            setPage(1)
            setSelectedDate(
              selectedDate
                ? formatIncidentDate(selectedDate, "YYYY-MM-DD")
                : formatIncidentDate(new Date(), "YYYY-MM-DD")
            )
          }}
        />
      </Box>
      <Grid container>
        <Grid item xs={12}>
          <NCTable
            columns={columns}
            data={reportsData || []}
            page={page}
            onNext={onNext}
            onPrev={onPrev}
            pageCount={total_pages}
            gotoPage={setPage}
            pageSize={pageSize || TABLE_CONFIG.PAGE_SIZE}
            setPageSize={setPageSize}
            sorting={ordering}
            setSorting={setOrdering}
          />
          {isLoading && <NCLoader open={true} />}
        </Grid>
      </Grid>
      <NCModal
        open={showPayModal}
        title={t("pay_pending_amount")}
        text={t("pay_pending_msg")}
        onClose={() => setShowPayModal(false)}
        submitButton={
          <NCButton
            variant="contained"
            color="secondary"
            disableElevation
            onClick={() => onPayClick()}
            style={{ width: "auto" }}
          >
            {t("common.confirm")}
          </NCButton>
        }
      />
    </Box>
  )
}

export default MyReports
